import KeyLine from 'components/shared/KeyLine';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { CalloutCardProps } from 'libs/contentful/props';
import { cn } from 'utils/styles';
import { CopyJustification } from 'types/constants';
import Link from 'components/shared/Link/Link';
import { liveUpdateAttrs } from 'libs/contentful/utils';

const CalloutCard = ({
  sys,
  className,
  showTopKeylineDesktop,
  showTopKeylineMobile,
  showBetweenKeylines,
  headline,
  headlineSmallRte,
  copy,
  attributionRte,
  children,
  defaultAlignment = 'row',
  copyJustification,
  link,
}: CalloutCardProps) => {
  const isLinkedCard = link != null;
  const cardClasses = cn(
    className,
    showBetweenKeylines &&
      'sm:pr-6 sm:border-r-2 sm:border-(--keyline-color) sm:border-opacity-20 sm:max-md:even:border-r-0 sm:last:border-r-0',
    isLinkedCard && 'rounded-none hover:text-current transition-all hover:opacity-80 focus:opacity-80',
  );
  const keylineClasses = cn(
    showTopKeylineDesktop && 'sm:block',
    !showTopKeylineDesktop && 'sm:hidden',
    showTopKeylineMobile && 'block',
    !showTopKeylineMobile && 'hidden',
  );

  const alignment = defaultAlignment === 'row' ? 'flex-row' : 'flex-col';

  const isSingleContentItem = [headline, headlineSmallRte, copy, attributionRte].filter(v => v != null).length === 1;

  const flexWrapperClasses = cn(
    `flex ${alignment} sm:flex-col max-sm:gap-x-7`,
    copyJustification === CopyJustification.Center ? 'items-center' : '',
    isSingleContentItem ? 'max-sm:items-center' : '', // only align center if single content (eg copy only), else align top
  );

  const copyWrapperClasses = cn(
    copyJustification === CopyJustification.Center ? 'sm:text-center' : 'text-left',
    'sm:mt-4 flex flex-col gap-2',
  );

  const Wrapper = isLinkedCard ? Link : 'div';
  const wrapperProps = isLinkedCard ? { href: link.url, internalPage: link.internalPage, isSubnav: true } : {};

  return (
    <Wrapper className={`${cardClasses}`} {...wrapperProps}>
      <div className={`keyline ${keylineClasses}`}>
        <KeyLine />
      </div>
      <div className={`${flexWrapperClasses}`}>
        {children}
        <div className={`${copyWrapperClasses}`}>
          {headline && (
            <div
              className={cn(
                'font-sans font-semibold text-heading-sm',
                isLinkedCard && 'relative flex flex-col items-center pr-3 underline',
              )}
              {...liveUpdateAttrs(sys?.id, 'headline')}>
              <RichTextHeadline headline={headline} level="h3" />
            </div>
          )}
          {headlineSmallRte && (
            <div
              className={cn('font-sans font-semibold text-body-sans', isLinkedCard && 'underline')}
              {...liveUpdateAttrs(sys?.id, 'headlineSmallRte')}>
              <RichTextHeadline headline={headlineSmallRte} />
            </div>
          )}
          {copy && (
            <div className="font-serif text-body-serif" {...liveUpdateAttrs(sys?.id, 'copy')}>
              <RichTextCopy copy={copy} ySpacing="none" />
            </div>
          )}
          {attributionRte && (
            <div className="font-sans font-medium" {...liveUpdateAttrs(sys?.id, 'attributionRte')}>
              &mdash; <RichTextCopy copy={attributionRte} ySpacing="none" inline />
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default CalloutCard;
